package com.tedd.db

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Long
import kotlin.String

public class SimulationQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> selectAll(mapper: (
    id: String,
    name: String,
    note: String?,
    order_index: Long,
    created_at: Long,
    updated_at: Long,
    is_deleted: Long,
    deleted_at: Long?,
    synced_at: Long?,
  ) -> T): Query<T> = Query(-1_007_456_444, arrayOf("simulation"), driver, "Simulation.sq",
      "selectAll",
      "SELECT simulation.id, simulation.name, simulation.note, simulation.order_index, simulation.created_at, simulation.updated_at, simulation.is_deleted, simulation.deleted_at, simulation.synced_at FROM simulation WHERE is_deleted = 0 ORDER BY order_index, created_at") {
      cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getLong(3)!!,
      cursor.getLong(4)!!,
      cursor.getLong(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7),
      cursor.getLong(8)
    )
  }

  public fun selectAll(): Query<Simulation> = selectAll { id, name, note, order_index, created_at,
      updated_at, is_deleted, deleted_at, synced_at ->
    Simulation(
      id,
      name,
      note,
      order_index,
      created_at,
      updated_at,
      is_deleted,
      deleted_at,
      synced_at
    )
  }

  public fun <T : Any> selectById(id: String, mapper: (
    id: String,
    name: String,
    note: String?,
    order_index: Long,
    created_at: Long,
    updated_at: Long,
    is_deleted: Long,
    deleted_at: Long?,
    synced_at: Long?,
  ) -> T): Query<T> = SelectByIdQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getLong(3)!!,
      cursor.getLong(4)!!,
      cursor.getLong(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7),
      cursor.getLong(8)
    )
  }

  public fun selectById(id: String): Query<Simulation> = selectById(id) { id_, name, note,
      order_index, created_at, updated_at, is_deleted, deleted_at, synced_at ->
    Simulation(
      id_,
      name,
      note,
      order_index,
      created_at,
      updated_at,
      is_deleted,
      deleted_at,
      synced_at
    )
  }

  public fun <T : Any> selectDirty(mapper: (
    id: String,
    name: String,
    note: String?,
    order_index: Long,
    created_at: Long,
    updated_at: Long,
    is_deleted: Long,
    deleted_at: Long?,
    synced_at: Long?,
  ) -> T): Query<T> = Query(-1_795_310_411, arrayOf("simulation"), driver, "Simulation.sq",
      "selectDirty",
      "SELECT simulation.id, simulation.name, simulation.note, simulation.order_index, simulation.created_at, simulation.updated_at, simulation.is_deleted, simulation.deleted_at, simulation.synced_at FROM simulation WHERE synced_at IS NULL OR synced_at < updated_at") {
      cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getLong(3)!!,
      cursor.getLong(4)!!,
      cursor.getLong(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7),
      cursor.getLong(8)
    )
  }

  public fun selectDirty(): Query<Simulation> = selectDirty { id, name, note, order_index,
      created_at, updated_at, is_deleted, deleted_at, synced_at ->
    Simulation(
      id,
      name,
      note,
      order_index,
      created_at,
      updated_at,
      is_deleted,
      deleted_at,
      synced_at
    )
  }

  public fun <T : Any> selectByIdAnyState(id: String, mapper: (
    id: String,
    name: String,
    note: String?,
    order_index: Long,
    created_at: Long,
    updated_at: Long,
    is_deleted: Long,
    deleted_at: Long?,
    synced_at: Long?,
  ) -> T): Query<T> = SelectByIdAnyStateQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2),
      cursor.getLong(3)!!,
      cursor.getLong(4)!!,
      cursor.getLong(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7),
      cursor.getLong(8)
    )
  }

  public fun selectByIdAnyState(id: String): Query<Simulation> = selectByIdAnyState(id) { id_, name,
      note, order_index, created_at, updated_at, is_deleted, deleted_at, synced_at ->
    Simulation(
      id_,
      name,
      note,
      order_index,
      created_at,
      updated_at,
      is_deleted,
      deleted_at,
      synced_at
    )
  }

  public fun insert(
    id: String,
    name: String,
    note: String?,
    order_index: Long,
    created_at: Long,
    updated_at: Long,
  ) {
    driver.execute(-324_227_366, """
        |INSERT INTO simulation(id, name, note, order_index, created_at, updated_at)
        |VALUES (?, ?, ?, ?, ?, ?)
        """.trimMargin(), 6) {
          bindString(0, id)
          bindString(1, name)
          bindString(2, note)
          bindLong(3, order_index)
          bindLong(4, created_at)
          bindLong(5, updated_at)
        }
    notifyQueries(-324_227_366) { emit ->
      emit("simulation")
    }
  }

  public fun update(
    name: String,
    note: String?,
    updated_at: Long,
    id: String,
  ) {
    driver.execute(20_718_826,
        """UPDATE simulation SET name = ?, note = ?, updated_at = ? WHERE id = ?""", 4) {
          bindString(0, name)
          bindString(1, note)
          bindLong(2, updated_at)
          bindString(3, id)
        }
    notifyQueries(20_718_826) { emit ->
      emit("simulation")
    }
  }

  public fun updateFromRemote(
    name: String,
    note: String?,
    order_index: Long,
    updated_at: Long,
    id: String,
  ) {
    driver.execute(-581_797_606,
        """UPDATE simulation SET name = ?, note = ?, order_index = ?, updated_at = ? WHERE id = ?""",
        5) {
          bindString(0, name)
          bindString(1, note)
          bindLong(2, order_index)
          bindLong(3, updated_at)
          bindString(4, id)
        }
    notifyQueries(-581_797_606) { emit ->
      emit("simulation")
    }
  }

  public fun softDelete(
    deleted_at: Long?,
    updated_at: Long,
    id: String,
  ) {
    driver.execute(-105_442_794,
        """UPDATE simulation SET is_deleted = 1, deleted_at = ?, updated_at = ? WHERE id = ?""", 3)
        {
          bindLong(0, deleted_at)
          bindLong(1, updated_at)
          bindString(2, id)
        }
    notifyQueries(-105_442_794) { emit ->
      emit("simulation")
    }
  }

  public fun restore(updated_at: Long, id: String) {
    driver.execute(1_974_233_741,
        """UPDATE simulation SET is_deleted = 0, deleted_at = NULL, updated_at = ? WHERE id = ?""",
        2) {
          bindLong(0, updated_at)
          bindString(1, id)
        }
    notifyQueries(1_974_233_741) { emit ->
      emit("simulation")
    }
  }

  public fun markSynced(
    synced_at: Long?,
    id: String,
    updated_at: Long,
  ) {
    driver.execute(1_097_450_824,
        """UPDATE simulation SET synced_at = ? WHERE id = ? AND updated_at <= ?""", 3) {
          bindLong(0, synced_at)
          bindString(1, id)
          bindLong(2, updated_at)
        }
    notifyQueries(1_097_450_824) { emit ->
      emit("simulation")
    }
  }

  private inner class SelectByIdQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("simulation", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("simulation", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_166_337_393,
        """SELECT simulation.id, simulation.name, simulation.note, simulation.order_index, simulation.created_at, simulation.updated_at, simulation.is_deleted, simulation.deleted_at, simulation.synced_at FROM simulation WHERE id = ? AND is_deleted = 0""",
        mapper, 1) {
      bindString(0, id)
    }

    override fun toString(): String = "Simulation.sq:selectById"
  }

  private inner class SelectByIdAnyStateQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("simulation", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("simulation", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> =
        driver.executeQuery(-1_017_696_844,
        """SELECT simulation.id, simulation.name, simulation.note, simulation.order_index, simulation.created_at, simulation.updated_at, simulation.is_deleted, simulation.deleted_at, simulation.synced_at FROM simulation WHERE id = ?""",
        mapper, 1) {
      bindString(0, id)
    }

    override fun toString(): String = "Simulation.sq:selectByIdAnyState"
  }
}
